import React, { useMemo } from 'react';
import { isFinite } from 'lodash';
import Image from 'next/image';
import { Box, Typography } from '@mui/material';

import styles from './WeatherCell.module.scss';

interface Props {
  condition: string | null;
  loading: boolean;
  icon: string | undefined;
  temperature: number | null;
  units: string | undefined;
}

const WeatherCell: React.FC<Props> = ({ icon, temperature, condition, units, loading }) => {
  const error = !isFinite(temperature);
  const loadingOrError = loading || error;
  const roundedTemperature = useMemo(() => Math.round(temperature || 0), [temperature]);

  const textColor = useMemo(
    () => (loadingOrError ? 'text.light' : 'text.primary'),
    [loadingOrError],
  );

  const text = useMemo(() => {
    const formattedUnits = units ? units?.toLowerCase() : '';
    return loadingOrError ? `--º${formattedUnits}` : `º${formattedUnits}`;
  }, [loadingOrError, units]);

  return (
    <Box className={styles.wrapper} data-testid="forecast-table-weather-wrapper">
      <Box className={styles.content} data-testid="forecast-table-weather-content">
        {loadingOrError ? null : (
          <Box className={styles.iconWrapper}>
            <Image
              alt="forecast weather icon"
              className={styles.icon}
              data-testid="forecast-table-weather-icon"
              height={20}
              src={`${icon}/${condition}.svg`}
              width={20}
            />
          </Box>
        )}
        <div
          className={styles.temperature}
          data-testid="forecast-table-weather-temperature"
          style={{ justifyContent: loadingOrError ? 'center' : 'start' }}
        >
          <Typography className={styles.textWrapper} component="span">
            {loadingOrError ? null : (
              <Typography color={textColor} component="h3" variant="subHeadline">
                {roundedTemperature}
              </Typography>
            )}
            <Typography
              className={styles.degrees}
              color={textColor}
              component="p"
              variant="caption2"
            >
              {text}
            </Typography>
          </Typography>
        </div>
      </Box>
    </Box>
  );
};

export default React.memo(WeatherCell);
